/**
 * The floating browser preview's hover pill: the recording dot, the panel
 * hand-off, and the dismissal, over the grab surface that drags the frame.
 *
 * Ported from t3code (`ThreadPreviewMiniPlayer.tsx`, `MiniPlayerShell`, MIT,
 * pin `e3e7cc3f`), split out of the shell to keep each file under the size
 * ratchet. The grab surface projects the 48px hit pad: its 32px bar stays the
 * desktop visual and the pad lifts the tappable area to the touch minimum for
 * every pointer, trackpads included (DESIGN.md -> "Mobile-First
 * Responsiveness").
 *
 * @module BrowserMiniPlayerPill
 */
import { PanelRightIcon, XIcon } from "lucide-react";

import { BROWSER_MINI_PLAYER_CHROME_Z_INDEX } from "./browserMiniPlayerLayout";
import type { PointerEventHandler } from "./useBrowserMiniPlayerGestures";
import { Button } from "~/components/ui/button";
import { Tooltip, TooltipPopup, TooltipTrigger } from "~/components/ui/tooltip";
import { HIT_AREA_PAD_CLASS } from "~/lib/touchTarget";
import { cn } from "~/lib/utils";

export interface BrowserMiniPlayerPillProps {
  readonly recording: boolean;
  readonly pillInset: number;
  readonly onOpenInPanel: () => void;
  readonly onDismiss: () => void;
  readonly beginGesture: PointerEventHandler;
  readonly handlePointerMove: PointerEventHandler;
  readonly endGesture: PointerEventHandler;
}

export function BrowserMiniPlayerPill(props: BrowserMiniPlayerPillProps) {
  return (
    <div
      className="group pointer-events-auto absolute size-3"
      style={{
        right: props.pillInset,
        top: props.pillInset,
        zIndex: BROWSER_MINI_PLAYER_CHROME_Z_INDEX,
      }}
    >
      <div
        role={props.recording ? "status" : undefined}
        aria-label={props.recording ? "Recording browser preview" : undefined}
        aria-hidden={!props.recording}
        className="absolute right-0 top-0 size-2 transition-opacity motion-reduce:transition-none group-hover:opacity-0 group-focus-within:opacity-0"
      >
        <span
          className={cn(
            "block size-2 rounded-full shadow-sm ring-1 ring-background/70",
            props.recording
              ? "bg-destructive motion-safe:animate-status-pulse"
              : "bg-foreground/25",
          )}
        />
      </div>
      <div
        className={cn(
          "pointer-events-none absolute right-0 top-0 flex h-8 cursor-grab items-center gap-0.5 rounded-lg border border-border/80 bg-popover/95 p-0.5 opacity-0 shadow-lg backdrop-blur-xl transition-opacity motion-reduce:transition-none group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100 active:cursor-grabbing",
          HIT_AREA_PAD_CLASS,
        )}
        onPointerDown={(event) => props.beginGesture(event)}
        onPointerMove={props.handlePointerMove}
        onPointerUp={props.endGesture}
        onPointerCancel={props.endGesture}
      >
        {props.recording ? (
          <span aria-hidden className="flex size-6 shrink-0 items-center justify-center">
            <span className="size-2 rounded-full bg-destructive motion-safe:animate-status-pulse" />
          </span>
        ) : null}
        <Tooltip>
          <TooltipTrigger
            render={
              <Button
                variant="ghost"
                size="icon-xs"
                // Above the grab surface's 48px hit pad, which would
                // otherwise intercept the press and start a drag instead.
                className="relative z-10"
                aria-label="Open browser in right panel"
                onPointerDown={(event) => event.stopPropagation()}
                onClick={props.onOpenInPanel}
              />
            }
          >
            <PanelRightIcon />
          </TooltipTrigger>
          <TooltipPopup side="top">Open in right panel</TooltipPopup>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger
            render={
              <Button
                variant="ghost"
                size="icon-xs"
                className="relative z-10"
                aria-label="Close floating browser preview"
                onPointerDown={(event) => event.stopPropagation()}
                onClick={props.onDismiss}
              />
            }
          >
            <XIcon />
          </TooltipTrigger>
          <TooltipPopup side="top">Close floating preview</TooltipPopup>
        </Tooltip>
      </div>
    </div>
  );
}
